import EventBus from "./event-bus";
import { EVENTS, PRODUCT_TYPES, base_url } from "./constants";
import DataStore from "./data-store";
import BackupService from "./backup-service";
import _ from "lodash";
import axios from "axios";
import ShopService from "./shop-service";
import growl from "./growl";
import { Product, TreeItem } from "../../types";
import Helper from "./Helper";
import UrlService from "./url-service";

export default {
	deleteChoice,
	activate,
	createIn,
	duplicate,
	getRoot,
	move,
	reorder,
	saveChoice,
	saveProduct,
	humanFileSize,
	resizeImage,
	uploadProductImage,
	splitExtension,
	appendTimestamp,
	deleteItem,
	saveReorder,
	saveMenuCategories,
	saveMenuCompos,
	saveJoinPrice,
	deleteJoinPrice,
	addCompo,
	removeCompo,
	getFlattenedTree,
	generateImage,
	getImageFile,
};

function removePrefix(s, delimiter) {
	// take off the prefix "15523_Products_"Sandwich
	let i = s.lastIndexOf(delimiter);
	return i == -1 ? s : s.substring(i + 1);
}

function getImageFile(product: Product) {
	let url = UrlService.imagePath(product.ShopId, product.ims);
	return Promise.all([fetch(url), removePrefix(product.ims, "_")])
		.then(([response, name]) => {
			if (!response.ok) {
				growl.error(`Erreur: ${response.statusText}`);
				return Promise.reject(response.statusText);
			}
			return Promise.all([response.blob(), name]);
		})
		.then(([blob, name]) => {
			blob.name = name;
			return blob;
		});
}

function generateImage(prompt: string, square = false): Promise<{ url: string; blob: Blob }> {
	async function readableStreamToString(stream) {
		const reader = stream.getReader();
		const decoder = new TextDecoder();
		let result = "";

		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			result += decoder.decode(value, { stream: true });
		}

		result += decoder.decode(); // Decode any remaining bytes
		return result;
	}
	growl.info("Prompt: " + prompt);
	return fetch(base_url + "/admin/image/generate-image", {
		method: "POST",
		body: JSON.stringify({ prompt, ShopId: ShopService.shop.ShopId, square }),
		headers: { "Content-Type": "application/json", Accept: "application/json" },
	})
		.then((stream) => {
			if (stream.ok) {
				let name = stream.headers.get("Content-Disposition");
				name = removePrefix(name, "=").replaceAll('"', "");
				return Promise.all([stream.blob(), name]);
			} else {
				return readableStreamToString(stream.body).then((err) => Promise.reject(JSON.parse(err)));
			}
		})
		.then(([blob, name]) => {
			blob.name = name;
			return blob;
		})
		.catch((err) => {
			console.log(err);
			growl.error("Erreur lors de la demande: " + err.message);
			return Promise.reject(err);
		});
}

function getFlattenedTree(item: TreeItem, indent = "-", currentIndent = "") {
	let arr = [];
	if (![PRODUCT_TYPES.Category, PRODUCT_TYPES.RootCategory].includes(item.product.ty) || item.product.Upsell) {
		return arr;
	}
	// item.safeIndentedName = this.$sce.trustAsHtml(indent + item.product.dn)
	arr.push({ item, title: currentIndent + item.product.dn });
	for (let child of item.childs) {
		arr = arr.concat(getFlattenedTree(child, indent, currentIndent + indent));
	}
	return arr;
}

function addCompo(menu, menuCategory, product) {
	if (menu.Upsell && menu.categories[0] == menuCategory && !Helper.isSuggestionsProduct(menu, ShopService.shop)) {
		// remove from old upsell, and put into new one
		if (product.upsell_to && product.upsell_to != menu.id) {
			let oldmenu = ShopService.shop.products[product.upsell_to];
			removeCompo(oldmenu, oldmenu?.categories[0], product);
		}
		product.upsell_to = menu.id;
		saveProduct({ product });
	}
	menuCategory.menuCompos.push(product.finalProducts[0]);
	saveMenuCompos(menuCategory);
}
function removeCompo(menu, menuCategory, product) {
	if (!menuCategory) {
		return;
	}
	_.remove(menuCategory.menuCompos, (menuCompo) => menuCompo.ProductId == product.id);
	saveMenuCompos(menuCategory);
	if (product.upsell_to == menu.id) {
		product.upsell_to = null;
		saveProduct({ product });
	}
}

function deleteChoice(OptionGroupId, OptionProductId) {
	return DataStore.post(
		base_url + "/admin/option-groups/delete-choice?ShopId=" + ShopService.shop.ShopId,
		{
			OptionGroupId,
			OptionProductId,
		},
		"Erreur à la suppression"
	);
}

function ensureUniqueProductName(newItem, existingItems) {
	function nameExists(newItem, existingItems) {
		for (let i in existingItems) {
			if (newItem.product.dn == existingItems[i].product.dn) {
				return true;
			}
		}
		return false;
	}
	while (nameExists(newItem, existingItems)) {
		newItem.product.dn = incrementString(newItem.product.dn);
	}
}

function incrementString(str) {
	var split = str.match(/(.*?)(\d*)$/);
	var num = split[2];
	str = split[1];
	return str + (+num + 1);
}

function activate(product) {
	let payload = _.pick(product, ["id", "av"]);
	return DataStore.post(base_url + "/admin/products/activate?ShopId=" + ShopService.shop.ShopId, payload, "Erreur à l'enregistrement");
}

function getMaxTid(tree, max = 0) {
	max = Math.max(tree.tid, max);
	for (let i in tree.childs) {
		max = getMaxTid(tree.childs[i], max);
	}
	return max;
}

function createIn(category, dn, ty, startEditing = true) {
	var productTree = this.getRoot(category);
	var newItem = {
		tid: getMaxTid(productTree) + 1,
		parent: category,
		childs: [],
		product: {
			ShopId: category.product.ShopId,
			dn: dn !== undefined ? dn : "Nouvelle Catégorie",
			dd: "",
			dp: 0,
			av: 1,
			ty: ty || 1,
			ims: "",
			options: [],
			categories: [], // in case its a menu
			Upsell: category.product.Upsell,
			allergens: [],
			consumables: [],
		},
	};
	ensureUniqueProductName(newItem, newItem.parent.childs);
	category.childs.push(newItem);

	if (startEditing) {
		EventBus.$emit(EVENTS.edit, newItem);
	}
	return newItem; // dont save to db now (only after edit)
}

function duplicate(item, nextTid) {
	// duplicate tree item
	var newItem = _.clone(item);
	delete newItem.$$hashKey; // avoid ng-repeat 'dupes' error
	// delete newItem.id
	newItem.childs = []; // empty from children
	var productTree = this.getRoot(item);
	newItem.tid = productTree.nextTreeId++;
	var siblings = item.parent.childs;
	// duplicate product
	newItem.product = _.clone(newItem.product);
	delete newItem.product.id;
	ensureUniqueProductName(newItem, newItem.parent.childs);
	insertAfter(siblings, item, newItem);
	return newItem; // dont save to db now (only after edit)
}

function insertAfter(list, item, newItem) {
	var pos = list.indexOf(item);
	list.splice(pos + 1, 0, newItem);
}

function getRoot(item) {
	while (item.parent) {
		item = item.parent;
	}
	return item;
}

function move(item, to_parent) {
	var from_parent = item.parent;
	BackupService.backup(to_parent.childs);
	_.remove(from_parent.childs, item);
	to_parent.childs.push(item);
	item.parent = to_parent;
}

function reorder(item, newPosition) {
	var childs = item.parent.childs;
	childs.splice(childs.indexOf(item), 1);
	childs.splice(newPosition, 0, item);
}

function saveChoice(product, optionGroup) {
	var choice = _.assign({}, product, {
		// ims: _.last(_.split(product.ims,'/')),
		categories: undefined,
		finalProducts: undefined,
		options: undefined,
	});
	var payload = {
		OptionGroupId: optionGroup.id,
		position: optionGroup.choices.indexOf(product.id) + 1,
		product: choice,
	};
	var url = base_url + "/admin/option-groups/save-choice?ShopId=" + product.ShopId;
	return DataStore.post(url, payload, `Echec de l'enregistrement du choix '${product.dn}'`);
}

function isRoot(item) {
	return !item?.parent;
}

function saveProduct(item) {
	var oldItem = BackupService.get(item);
	let move = null;
	if (item.parent) {
		move = {
			from_parent: isRoot(oldItem?.parent) ? 0 : oldItem && oldItem.parent.product.id,
			to_parent: isRoot(item.parent) ? 0 : item.parent.product.id,
			siblings_ids: _.map(item.parent.childs, "id"),
		};
	}
	_.defaults(item.product, { Promo: false, NoImg: false });
	let payload = {
		ShopId: item.product.ShopId,
		move: move,
		product: _.omit(item.product, ["categories", "finalProducts"]),
	};
	return DataStore.post(base_url + "/admin/products/save", payload, "Echec de l'enregistrement").then(function (data) {
		if (item.product.ty == 2) {
			// its a menu
			DataStore.getMenus(item.product.ShopId).then(function (menus) {
				menus[item.product.id] = item.product;
				item.product.joinPrices = item.product.joinPrices || {};
			});
		}
		if (!item.product.id) {
			// newly created product
			item.product.id = data.product.id;
			item.id = data.product.id;
			ShopService.shop.products[item.product.id] = item.product;
		}
		BackupService.commit();
		EventBus.$emit(EVENTS.itemChanged, item);
		return item;
	});
}

function humanFileSize(fileSizeInBytes) {
	var i = -1;
	var byteUnits = [" kB", " MB", " GB", " TB", "PB", "EB", "ZB", "YB"];
	do {
		fileSizeInBytes = fileSizeInBytes / 1024;
		i++;
	} while (fileSizeInBytes > 1024);
	return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
}

function resizeImage(imageFile, toMaxWidth) {
	var filename = imageFile.name;
	var imageTag = new Image();
	var canvas = document.createElement("canvas");
	var promise = new Promise((resolve, reject) => {
		imageTag.onerror = (err) => {
			reject(err);
			console.log(err);
		};
		imageTag.onload = (ev) => {
			let toWidth = Math.min(imageTag.width, toMaxWidth);
			var ratio = imageTag.width / imageTag.height;
			var toHeight = toWidth / ratio;
			canvas.width = toWidth;
			canvas.height = toHeight;
			canvas.getContext("2d").drawImage(imageTag, 0, 0, toWidth, toHeight);
			canvas.toBlob((blob) => {
				blob.name = filename;
				let oldsize = this.humanFileSize(imageFile.size);
				let newsize = this.humanFileSize(blob.size);
				growl.info("Réduction du poids de l'image de " + oldsize + " à " + newsize);
				resolve(blob);
			}, "image/jpeg");
		};
		imageTag.src = URL.createObjectURL(imageFile);
	});
	return promise;
}

function uploadProductImage(product, imageFile) {
	if (!imageFile) {
		return Promise.resolve({
			data: {
				status: "OK",
			},
		});
	} else {
		var imagePromise;
		if (imageFile.size > 500 * 1024) {
			growl.info("Image supérieure à 500Ko, réduction du poids");
			imagePromise = this.resizeImage(imageFile, 1340);
		} else {
			imagePromise = Promise.resolve(imageFile);
		}
		// ensure product is saved so it has an id before uploading image
		if (!product.id) {
			imagePromise = imagePromise.then((imageFile) => saveProduct({ product }).then((res) => imageFile));
		}
		return imagePromise
			.then((imageFile) => {
				if (!product.id) {
					let res = {
						data: { status: "KO", msg: "Enregistrez d'abord le produit sans image, puis uploader une image ensuite" },
					};
					return Promise.reject(res);
				}
				let formData = new FormData();
				formData.append("id", product.id || null);
				formData.append("filename", imageFile.name.split("?")[0]);
				formData.append("file", imageFile);
				let config = {
					headers: { "Content-Type": "multipart/form-data" },
				};
				return axios.post(base_url + "/admin/image/upload-product-image?ShopId=" + product.ShopId, formData, config);
				// return Upload.upload({
				// 	url: base_url + '/admin/image/upload-product-image?ShopId=' + product.ShopId,
				// 	file: imageFile,
				// 	data: {
				// 		id: product.id,
				// 		// strip the querystring, if any
				// 		filename: imageFile.name.split('?')[0]
				// 	},
				// })
			})
			.then((res) => (res.data.status == "OK" ? res.data : Promise.reject(res.data.msg)))
			.then((data) => {
				if (data.filename) {
					// append unique querystring to force cache update
					// see https://stackoverflow.com/questions/1431512/is-there-a-way-to-force-browsers-to-refresh-download-images
					//  + '?' + _.uniqueId()
					product.ims = data.filename;
				}
			})
			.catch((err) => {
				console.error(err);
				Promise.reject(growl.error("Echec de l'upload d'image"));
			});
	}
}

function splitExtension(filename) {
	let parts = filename.split(".");
	if (parts.length == 1) {
		return parts;
	} else {
		return [parts.slice(0, -1).join("."), _.last(parts)];
	}
}

function appendTimestamp(filename) {
	let path = this.splitExtension(filename);
	let nowString = moment().format("YYYYMMDD-HHmmss");
	let updated = path[0].replace(/[0-9]{8}-[0-9]{6}/, nowString);
	if (updated == path[0]) {
		updated = path[0] + "_" + nowString;
	}
	path[0] = updated;
	return path.join(".");
}

function deleteItem(item) {
	if (item.childs.length) {
		var msg = "La catégorie n'est pas vide: impossible de supprimer";
		growl.warning(msg);
		return Promise.reject(msg);
	} else {
		var promise;
		if (item.product.id) {
			let payload = {
				ShopId: item.product.ShopId,
				ParentId: isRoot(item.parent) ? 0 : item.parent.product.id,
				ProductId: item.product.id,
			};
			promise = DataStore.post(base_url + "/admin/tree/delete-child", payload, "Erreur à la suppression");
		} else {
			promise = Promise.resolve({
				data: {
					status: "OK",
				},
			});
		}
		return promise.then(function (data) {
			// remove from tree
			_.remove(item.parent.childs, item);
			// remove finalprods from dataStore, and menuCompos as well
			DataStore.getFinalProducts(item.product.ShopId).then(function (finalProducts) {
				_.forEach(item.product.finalProducts, function (finalProduct) {
					delete finalProducts[finalProduct.FinalProductId];
				});
			});
			// remove menuCompo from dataStore's menus
			DataStore.getMenus(item.product.ShopId).then(function (menus) {
				_.forEach(menus, function (menu) {
					_.forEach(menu.categories, function (category) {
						category.menuCompos = _.difference(category.menuCompos, item.product.finalProducts);
					});
				});
			});
			EventBus.$emit(EVENTS.itemChanged, item);
			if (item.product.id) {
				growl.success(item.product.dn + " supprimé");
			}
			return data;
		});
	}
}

function saveReorder(item) {
	let payload = {
		ShopId: item.product.ShopId,
		ParentId: isRoot(item.parent) ? 0 : item.parent.product.id,
		children_ids: _.map(item.parent.childs, "id"),
	};
	return DataStore.post(base_url + "/admin/tree/reorder-children", payload, "Erreur à l'enregistrement").then((res) =>
		growl.success(item.product.dn + " déplacé")
	);
}

function saveMenuCategories(menu) {
	var payload = {
		id: menu.id,
		categories: _.map(menu.categories, (menuCat) =>
			_.assign({}, _.omit(menuCat, ["treeItem", "menuCompos", "$$hashKey"]), { menu_compo_ids: _.map(menuCat.menuCompos, "FinalProductId") })
		),
	};

	return DataStore.post(base_url + "/admin/menus/categories?ShopId=" + menu.ShopId, payload, "Erreur à l'enregistrement des catégories").then(
		function (data) {
			// fetch new MenuCategoryId's
			for (var i in data.categories) {
				menu.categories[i].MenuCategoryId = data.categories[i].MenuCategoryId;
			}
		}
	);
}

function saveMenuCompos(category) {
	return DataStore.post(
		base_url + "/admin/menus/menu-compos?ShopId=" + ShopService.shop.ShopId,
		{
			MenuCategoryId: category.MenuCategoryId,
			menu_compo_ids: _.map(category.menuCompos, "FinalProductId"),
		},
		"Erreur à la sauvegarde de la composition du menu"
	);
}

function saveJoinPrice(joinPrice) {
	return DataStore.post(
		DataStore.urlize("menus/join-price", {
			ShopId: ShopService.shop.ShopId,
		}),
		joinPrice,
		"Impossible d'enregistrer le prix"
	);
}

function deleteJoinPrice(joinPrice) {
	return DataStore.post(
		DataStore.urlize("menus/join-price-delete", {
			ShopId: ShopService.shop.ShopId,
		}),
		joinPrice,
		"Impossible d'enregistrer le prix"
	);
}
